/**
 * Metarr — Übersicht in der Form der Vorlage.
 *
 * Rabe und Titel, das Zahlenband mit den wartenden Entscheidungen, die
 * Liste „Zur Entscheidung" mit den offenen Vorschlägen, die Läufe der
 * letzten 24 Stunden, die Hausmeisterberichte und das Grenzen-Band am
 * Fuß: was Metarr nicht darf, steht dort, damit die Seite nicht nur
 * verspricht, sondern begründet.
 *
 * Die Konfiguration (Anbieterverbindungen, Modellrouting, Hausmeister)
 * liegt in den Einstellungen unter dem Reiter „Metarr".
 */

import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/Button";
import { Seitenkopf } from "@/components/entwurf/Seitenkopf";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";
import { Kennzahl } from "@/components/entwurf/Kennzahl";
import { Zahlenband, Nebenwert } from "@/components/entwurf/Zahlenband";
import { Rubrik } from "@/components/entwurf/Rubrik";
import { Rueckfragekarte } from "@/components/entwurf/Rueckfragekarte";
import { RabeAvatar } from "@/components/RabeAvatar";
import { cx, focusRing } from "@/lib/utils";
import { ProjektplanAbschnitt } from "./ProjektplanAbschnitt";
import { CodeArbeitspaketAbschnitt } from "./metarr/CodeArbeitspaketAbschnitt";

const aktiveLaufZustaende = new Set(["wartet", "schreibt", "laeuft"]);

const aktionsNamen: Record<string, string> = {
  task_create: "Aufgabe anlegen",
  aufgabe_anlegen: "Aufgabe anlegen",
  akzeptanzkriterium_anlegen: "Akzeptanzkriterium anlegen",
  seite_anlegen: "Seite anlegen",
};

/** Wortlaut der Grenzen — was Metarr nicht darf, aus Paket 9. */
const GRENZEN = [
  "Metarr bestätigt seinen eigenen Vorschlag nicht selbst.",
  "Eine Fassung, die sich seit der Analyse geändert hat, wird nicht überschrieben.",
  "Aussagen brauchen einen wörtlichen Beleg aus tatsächlich gelesenem Kontext.",
  "Nicht sichtbare Projekte und nicht erreichbare Anbieter werden gemeldet, nicht umgangen.",
  "Was nicht geprüft wurde, gilt als unvollständig — und wird als solches gezeigt.",
] as const;

function vorZeit(wert: string | Date | null | undefined): string {
  if (!wert) return "—";
  const minuten = Math.max(
    0,
    Math.floor((Date.now() - new Date(wert).getTime()) / 60_000),
  );
  if (minuten < 1) return "gerade eben";
  if (minuten < 60) return `vor ${minuten} Min.`;
  const stunden = Math.floor(minuten / 60);
  if (stunden < 24) return `vor ${stunden} Std.`;
  const tage = Math.floor(stunden / 24);
  return `vor ${tage} Tagen`;
}

export function MetarrSeite() {
  const client = useQueryClient();
  const [aufgeklappt, setzeAufgeklappt] = useState<string | null>(null);

  const entwuerfe = useQuery({
    queryKey: ["cubic", "metarr", "entwuerfe"],
    queryFn: () => api.metarr.entwuerfe.query(),
    refetchInterval: (abfrage) =>
      abfrage.state.data?.some((entwurf) =>
        aktiveLaufZustaende.has(entwurf.zustand),
      )
        ? 1_000
        : false,
  });

  const vorschlaege = useQuery({
    queryKey: ["cubic", "metarr", "vorschlaege"],
    queryFn: () => api.metarr.vorschlaege.query(),
    refetchInterval: 2_000,
  });

  const hausmeister = useQuery({
    queryKey: ["cubic", "metarr", "hausmeister-laeufe"],
    queryFn: () => api.metarr.hausmeisterLaeufe.query(),
  });

  const alleEntwuerfe = entwuerfe.data ?? [];
  const offeneVorschlaege = useMemo(
    () =>
      (vorschlaege.data ?? []).filter(
        (vorschlag) => vorschlag.zustand === "offen",
      ),
    [vorschlaege.data],
  );
  const rückfragen = alleEntwuerfe.filter(
    (entwurf) => entwurf.rueckfrage != null && entwurf.rueckfrage.length > 0,
  );
  const gescheitert = alleEntwuerfe.filter(
    (entwurf) => entwurf.zustand === "gescheitert",
  );
  const verbrauchHeute = alleEntwuerfe.reduce(
    (summe, entwurf) =>
      entwurf.tokenVerbrauch != null &&
      entwurf.angelegtAm &&
      new Date(entwurf.angelegtAm).toDateString() === new Date().toDateString()
        ? summe + entwurf.tokenVerbrauch
        : summe,
    0,
  );
  const lauf24h = alleEntwuerfe.filter(
    (entwurf) =>
      entwurf.angelegtAm &&
      Date.now() - new Date(entwurf.angelegtAm).getTime() < 24 * 60 * 60 * 1_000,
  );

  const hausmeisterBerichte = hausmeister.data ?? [];

  const annehmen = useMutation({
    mutationFn: (vorschlagId: string) =>
      api.metarr.annehmen.mutate({ vorschlagId }),
    onSuccess: async () => {
      setzeAufgeklappt(null);
      await Promise.all([vorschlaege.refetch(), entwuerfe.refetch()]);
      await client.invalidateQueries({ queryKey: ["cubic", "navigation"] });
    },
  });
  const ablehnen = useMutation({
    mutationFn: (vorschlagId: string) =>
      api.metarr.ablehnen.mutate({ vorschlagId }),
    onSuccess: async () => {
      setzeAufgeklappt(null);
      await Promise.all([vorschlaege.refetch(), entwuerfe.refetch()]);
      await client.invalidateQueries({ queryKey: ["cubic", "navigation"] });
    },
  });
  const beantworten = useMutation({
    mutationFn: (eingabe: { entwurfId: string; antwort: string }) =>
      api.metarr.rueckfrageBeantworten.mutate(eingabe),
    onSuccess: async () => {
      await entwuerfe.refetch();
      await client.invalidateQueries({ queryKey: ["cubic", "navigation"] });
    },
  });
  const abbrechen = useMutation({
    mutationFn: (eingabe: { entwurfId: string }) =>
      api.metarr.rueckfrageAbbrechen.mutate(eingabe),
    onSuccess: async () => {
      await entwuerfe.refetch();
      await client.invalidateQueries({ queryKey: ["cubic", "navigation"] });
    },
  });

  return (
    <div>
      {/*
        Der Rabe steht rechts, nicht links vor dem Titel. Gemessen hatte er die
        Überschrift um 52px nach rechts geschoben (38px Zeichen plus 14px
        Abstand) — auf allen anderen Seiten beginnt sie bei derselben linken
        Kante. Als Aktion rechts bleibt er sichtbar, ohne die Kante zu brechen.
      */}
      <Seitenkopf
        kontext="Der ruhige Projektassistent"
        aktion={<RabeAvatar groesse={38} />}
      >
        Metarr
      </Seitenkopf>
      <p className="mt-4 max-w-[62ch] text-[15.5px] font-light leading-[1.7] text-tinte2">
        Metarr ist der ruhige Projektassistent in cubic studio. Er sammelt
        Kontext, bereitet Arbeit vor, prüft Zusammenhänge und übernimmt
        wiederkehrende Qualitätsarbeit. Er meldet sich nur bei Entscheidungen,
        Widersprüchen oder echten Aufhaltern. Änderungen bleiben nachvollziehbar
        und werden — solange keine bewusst aktivierte Regel etwas anderes
        festlegt — von einem Menschen bestätigt.
      </p>

      {/* Zahlenband. */}
      <Zahlenband className="mt-9">
        <Kennzahl
          label="Zur Entscheidung"
          grad={56}
          wert={String(offeneVorschlaege.length)}
          ton={offeneVorschlaege.length > 0 ? "akzent" : undefined}
        />
        <Kennzahl
          label="Rückfragen"
          grad={56}
          wert={String(rückfragen.length)}
          ton={rückfragen.length > 0 ? "akzent" : undefined}
        />
        <Kennzahl
          label="Gescheitert"
          grad={56}
          wert={String(gescheitert.length)}
          ton={gescheitert.length > 0 ? "warn" : undefined}
        />
        <div className="flex min-w-[280px] flex-1 flex-wrap gap-x-[22px] gap-y-1.5 pb-2">
          <Nebenwert wert={verbrauchHeute} label="Token heute" />
          <Nebenwert wert={lauf24h.length} label="Läufe (24 h)" />
        </div>
      </Zahlenband>

      <ProjektplanAbschnitt />

      <CodeArbeitspaketAbschnitt />

      {/*
        Rückfragen stehen **vor** „Zur Entscheidung": Ein Lauf, der wartet, ist
        blockiert — er kommt ohne Antwort keinen Schritt weiter. Ein Vorschlag
        dagegen liegt fertig da und kann liegen bleiben.

        Vorher war die Rückfrage auf dieser Seite nur eine Zahl im Zahlenband
        und ein Wort in der Vorschlagsliste („Rückfrage wartet"). Beantworten
        ließ sie sich einzig im Assistenten-Abschnitt der Aufgabenseite — und
        für die Projektplanung, die gar keine Aufgabe hat, damit überhaupt
        nicht.
      */}
      {rückfragen.length > 0 ? (
        <div className="mt-13">
          <Rubrik anzahl={`${rückfragen.length} offen`}>Rückfragen</Rubrik>
          <div className="space-y-4">
            {rückfragen.map((entwurf) => (
              <div key={entwurf.id}>
                {entwurf.satz ? (
                  <p className="mb-2.5 text-[13px] leading-[1.5] text-tinte3">
                    {entwurf.satz}
                  </p>
                ) : null}
                <Rueckfragekarte
                  frage={entwurf.rueckfrage!}
                  entscheidung={entwurf.rueckfrageEntscheidung}
                  optionen={entwurf.rueckfrageOptionen}
                  mehrfach={entwurf.rueckfrageMehrfach}
                  laeuft={beantworten.isPending}
                  onAntworten={(antwort) =>
                    beantworten.mutate({ entwurfId: entwurf.id, antwort })
                  }
                  onAbbrechen={() =>
                    abbrechen.mutate({ entwurfId: entwurf.id })
                  }
                  abbruchLaeuft={
                    abbrechen.isPending &&
                    abbrechen.variables?.entwurfId === entwurf.id
                  }
                />
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* Zur Entscheidung. */}
      <div className="mt-13">
        <Rubrik anzahl={`${offeneVorschlaege.length} offen`}>
          Zur Entscheidung
        </Rubrik>
        {offeneVorschlaege.length === 0 ? (
          <div className="border-b border-linie py-6">
            <Mikrolabel grad={11.5}>
              Nichts wartet auf eine Entscheidung. Wenn Metarr etwas vorbereitet
              hat, steht es hier.
            </Mikrolabel>
          </div>
        ) : (
          offeneVorschlaege.map((vorschlag) => {
            const entwurf = alleEntwuerfe.find(
              (e) => e.id === vorschlag.entwurfId,
            );
            const anzahlBelege = Array.isArray(vorschlag.quellen)
              ? vorschlag.quellen.length
              : 0;
            return (
              <div
                key={vorschlag.id}
                className="border-b border-linie py-[15px]"
              >
                <div className="flex items-start gap-3.5">
                  <span className="min-w-0 flex-1">
                    <Mikrolabel grad={11} className="text-akzent">
                      {aktionsNamen[vorschlag.aktion] ?? vorschlag.aktion}
                    </Mikrolabel>
                    <p className="mt-1 text-[14px] leading-[1.5] text-tinte">
                      {vorschlag.felder &&
                      typeof vorschlag.felder === "object" &&
                      "titel" in vorschlag.felder &&
                      typeof vorschlag.felder.titel === "object" &&
                      vorschlag.felder.titel !== null &&
                      "wert" in vorschlag.felder.titel
                        ? String(
                            (vorschlag.felder.titel as { wert?: unknown }).wert ??
                              "",
                          )
                        : vorschlag.begruendung}
                    </p>
                    {vorschlag.begruendung ? (
                      <p className="mt-1.5 max-w-[58ch] text-[13px] font-light leading-[1.55] text-tinte3">
                        {vorschlag.begruendung}
                      </p>
                    ) : null}
                    <div className="mt-2 flex flex-wrap items-center gap-[14px]">
                      <Mikrolabel grad={11}>
                        {anzahlBelege}{" "}
                        {anzahlBelege === 1 ? "Beleg" : "Belege"}
                      </Mikrolabel>
                      <Mikrolabel grad={11}>
                        {entwurf ? vorZeit(entwurf.angelegtAm) : ""}
                      </Mikrolabel>
                      {entwurf?.rueckfrage ? (
                        <Mikrolabel grad={11} className="text-akzent">
                          Rückfrage wartet
                        </Mikrolabel>
                      ) : null}
                    </div>
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setzeAufgeklappt(
                        aufgeklappt === vorschlag.id ? null : vorschlag.id,
                      )
                    }
                    aria-expanded={aufgeklappt === vorschlag.id}
                    className={cx(
                      "mt-0.5 flex-none cursor-pointer font-mono text-[11.5px] text-akzent hover:underline",
                      focusRing,
                    )}
                  >
                    {aufgeklappt === vorschlag.id ? "Schließen" : "Ansehen"}
                  </button>
                </div>
                {aufgeklappt === vorschlag.id ? (
                  <div className="mt-4 space-y-4">
                    {Array.isArray(vorschlag.quellen) &&
                    vorschlag.quellen.length > 0 ? (
                      <div className="space-y-1.5">
                        <Mikrolabel grad={11} className="text-tinte4">
                          Quellenbelege
                        </Mikrolabel>
                        {vorschlag.quellen.map((quelle, i) => (
                          <div
                            key={i}
                            className="bg-wisch px-4 py-2.5 font-mono text-[11px] leading-[1.6] text-tinte3"
                          >
                            {quelle.art}:{quelle.id} — „{quelle.beleg}"
                          </div>
                        ))}
                      </div>
                    ) : null}
                    <div className="flex flex-wrap items-center gap-2.5">
                      <Button
                        className="h-[29px] px-3.5 font-mono text-[11.5px]"
                        onClick={() => annehmen.mutate(vorschlag.id)}
                        disabled={annehmen.isPending}
                      >
                        Annehmen
                      </Button>
                      <Button
                        variant="ghost"
                        className="h-[29px] px-3.5 font-mono text-[11.5px]"
                        onClick={() => ablehnen.mutate(vorschlag.id)}
                        disabled={ablehnen.isPending}
                      >
                        Ablehnen
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })
        )}
      </div>

      {/* Läufe letzte 24 Stunden. */}
      <div className="mt-13">
        <Rubrik anzahl={`${lauf24h.length} Läufe`}>
          Läufe letzte 24 Stunden
        </Rubrik>
        {lauf24h.length === 0 ? (
          <div className="border-b border-linie py-6">
            <Mikrolabel grad={11.5}>
              Keine Läufe in den letzten 24 Stunden.
            </Mikrolabel>
          </div>
        ) : (
          lauf24h.map((entwurf) => (
            <div
              key={entwurf.id}
              className="flex items-center gap-3.5 border-b border-linie py-3"
            >
              <span
                aria-hidden="true"
                className={cx(
                  "size-[5px] flex-none",
                  entwurf.zustand === "gescheitert"
                    ? "bg-akzent"
                    : aktiveLaufZustaende.has(entwurf.zustand)
                      ? "bg-tinte"
                      : "bg-ruhe",
                )}
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13.5px] text-tinte">
                  {entwurf.satz}
                </span>
                <span className="mt-0.5 block font-mono text-[10.5px] text-tinte4">
                  {entwurf.modell ?? "Modell"} ·{" "}
                  {entwurf.dauerMs != null
                    ? `${(entwurf.dauerMs / 1000).toFixed(1)} s`
                    : "läuft …"}
                  {entwurf.tokenVerbrauch != null
                    ? ` · ${entwurf.tokenVerbrauch} Token`
                    : ""}
                </span>
              </span>
              <Mikrolabel grad={11} className="flex-none">
                {vorZeit(entwurf.angelegtAm)}
              </Mikrolabel>
            </div>
          ))
        )}
      </div>

      {/* Hausmeisterberichte. */}
      <div className="mt-13">
        <Rubrik anzahl={`${hausmeisterBerichte.length} Berichte`}>
          Hausmeisterberichte
        </Rubrik>
        {hausmeisterBerichte.length === 0 ? (
          <div className="border-b border-linie py-6">
            <Mikrolabel grad={11.5}>
              Noch keine Hausmeisterberichte. Starte einen Lauf in den
              Einstellungen unter Metarr.
            </Mikrolabel>
          </div>
        ) : (
          hausmeisterBerichte.slice(0, 10).map((bericht) => {
            const ergebnis = bericht.ergebnis as
              | Record<string, unknown>
              | undefined;
            const befundZahl =
              typeof ergebnis?.befunde === "number"
                ? ergebnis.befunde
                : Array.isArray(ergebnis?.befunde)
                  ? ergebnis.befunde.length
                  : null;
            return (
              <div
                key={bericht.id}
                className="flex items-center gap-3.5 border-b border-linie py-3"
              >
                {/*
                  `w-[64px]` war zu schmal: `beziehungen_pruefen` sind 19
                  Zeichen, in Plex Mono bei Grad 11 rund 125px. Zusammen mit
                  `flex-none` und ohne `truncate` lief der Name aus seinem
                  Kasten heraus und schob sich über den Zustand daneben.

                  Die feste Spalte bleibt — sie hält die Zustände untereinander
                  auf einer Linie —, aber breit genug für den längsten Wert und
                  mit `truncate` als Sicherung, falls eine Art dazukommt, die
                  länger ist.
                */}
                <Mikrolabel grad={11} className="w-[140px] flex-none truncate">
                  {bericht.art}
                </Mikrolabel>
                <Mikrolabel grad={11} className="flex-none">
                  {bericht.zustand}
                </Mikrolabel>
                {befundZahl != null ? (
                  <Mikrolabel grad={11} className="flex-none">
                    {befundZahl} Befunde
                  </Mikrolabel>
                ) : null}
                <Mikrolabel grad={11} className="ml-auto flex-none">
                  {vorZeit(bericht.angelegtAm)}
                </Mikrolabel>
              </div>
            );
          })
        )}
      </div>

      {/* Grenzen-Band. */}
      <div className="mt-14 bg-tafel px-9 py-9 text-tafel-tinte">
        <Mikrolabel grad={12.5} className="text-tafel-tinte">
          Metarr entscheidet nicht. Er bereitet vor.
        </Mikrolabel>
        <ul role="list" className="mt-5 space-y-2.5">
          {GRENZEN.map((grenze) => (
            <li key={grenze} className="flex items-start gap-3">
              <span
                aria-hidden="true"
                className="mt-[7px] size-[5px] flex-none bg-tafel-akzent"
              />
              <span className="text-[13.5px] font-light leading-[1.6] text-tafel-tinte3">
                {grenze}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
